import { useState, type ReactNode } from 'react';
import { cn } from '@lib/utils';
import { ImgWithFallback } from '@components/ui/img-with-fallback';
import { EVENT_JOIN_CTA_CLASS } from './eventCtaStyles';
import { LiveCardPauseContext } from './liveCardPause';
import { FOCUS_RING_CLASSES, PRESS_FEEDBACK_CLASSES } from './liveMotion';

/**
 * The card's row-3 element (W2.7; the LAST row of the card's RIGHT COLUMN, under
 * the rotating content row — Dexter 2026-07-23 layout fix). A TAPPABLE CTA in two
 * visual kinds, or a non-interactive muted STATUS line — one union so both render
 * in the SAME slot. The card HUGS its content (Dexter same-day follow-up), so the
 * status variant is simply a SHORTER card — no filler height equalizing it with
 * the CTA variant:
 *   - `primary` = the filled-teal attend CTA (`EVENT_JOIN_CTA_CLASS`, shared with
 *     the event-page hero) — "Join event" / "I am here".
 *   - `soft`    = the subdued recap CTA (mb-yellow-soft) — "I was here".
 *   - `status`  = a muted, non-interactive line — the goer's "You're going".
 */
export type LiveCompactCardCta =
  | { kind: 'primary' | 'soft'; label: string; onTap: () => void }
  | { kind: 'status'; label: string };

export interface LiveCompactCardProps {
  title: string;
  coverUrl?: string;
  /**
   * The phase badge floated top-left ON the cover image (Figma 6940:18935) —
   * `EventPhaseBadge` variant `card-corner`, composed by the host so this card
   * mounts no clock. Renders red "Live" / the "Starts in Xh" countdown; silent
   * (null) for the ended recap, whose body copy carries the recap instead.
   */
  badge?: ReactNode;
  /**
   * The single content row under the title (Figma: image-left, title, ONE row).
   * The soon/live cells pass the rotating `LiveSlotContentRow`; the recap passes
   * its static "You were there…" line. Kept inside the tap body so its text (or
   * the rotating row's sr-only line) joins the card's accessible name.
   */
  children: ReactNode;
  /**
   * Rendered as the LAST row of the card's RIGHT COLUMN, under the content row
   * (Dexter 2026-07-23 — was first a horizontal sibling that squeezed the going
   * text, then briefly a full-CARD-width row below that left a dead band under the
   * image; now it lives in the right column so the image spans the card's full
   * height); nothing renders when absent (no chevron — Figma 6940:18935).
   */
  cta?: LiveCompactCardCta;
  /**
   * Tap anywhere on the CARD — image, gaps and status line included — navigates
   * to the event page (L7; Dexter 2026-07-23: the whole card is the tap target).
   * The interactive row-3 CTA keeps its own handler.
   */
  onTap: () => void;
}

/**
 * The space-slot card, v2 shape (Figma 6940:18935, date row removed by Dexter):
 * a ~112px-wide cover on the LEFT that spans the card's FULL height (teal-accented,
 * left-radiused) with the phase chip floated in its top-left corner, and a RIGHT
 * COLUMN beside it that stacks — title → content row → row-3 (the CTA/status) —
 * each full COLUMN width (Dexter 2026-07-23 layout fix: the row-3 CTA used to be a
 * full-CARD-width row BELOW everything, which left the image spanning only the top
 * and a dead band beneath it).
 *
 * HUG (Dexter same-day follow-up №2): the right column is a plain TOP-ANCHORED
 * stack — ONE uniform gap-1.5 between its rows and a modest py-2.5, no justify
 * distribution, no spacers, no min/fixed heights anywhere on the shell — so the
 * card is exactly as tall as the stacked rows (a status card is shorter than a
 * CTA card; the CTA's ≥44px hit box is margin-collapsed out of layout). gap-1.5
 * (6px) matches the pre-existing title→content rhythm and lands the CTA variant
 * at 108px — the Figma cover height.
 *
 * WHOLE-CARD TAP (Dexter same-day follow-up №3): the ROOT delegates clicks to
 * `onTap` (the SimpleEventCard/EventCardShell house pattern), so the image, gaps
 * and status line all navigate — minus their `role="button"` root: the REAL body
 * button below stays the single AT/keyboard control (its accessible name = title +
 * content), so there is no nested-interactive violation. The root handler bails on
 * any click on/inside a `<button>`: the body button can't double-fire and the CTA
 * never navigates (belt on top of its own stopPropagation). Body button and CTA
 * remain independent flex siblings — never nested, hit areas never overlap.
 *
 * The card is content-agnostic (composition over flags): the host supplies the
 * `badge` and the content-row `children`, so the same shell dresses the
 * rotating soon/live cells and the static recap cell. It mounts no clock — the
 * badge and the rotating row ride the host's `useNow` clocks.
 */
export function LiveCompactCard({
  title,
  coverUrl,
  badge,
  children,
  cta,
  onTap,
}: LiveCompactCardProps) {
  // Pause the content-row rotation while the card is hovered or contains focus
  // (WCAG 2.2.2). Two flags combined so releasing one channel (mouse leaves)
  // never resumes while the other (keyboard focus) still holds the card.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  return (
    <LiveCardPauseContext value={hovered || focused}>
      <div
        // Whole-card tap (Dexter 2026-07-23): the root delegates to onTap so the
        // image, gaps and status line navigate too — the house EventCardShell/
        // SimpleEventCard root-click pattern, minus role="button" (the real body
        // button below is the AT control; a role'd root would nest interactives).
        // Bail on any click on/inside a <button>: the body button already fires
        // onTap itself (no double-fire), and the CTA owns its own tap (belt on
        // top of its stopPropagation).
        onClick={event => {
          if ((event.target as HTMLElement).closest('button')) {
            return;
          }
          onTap();
        }}
        onMouseEnter={() => {
          setHovered(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
        }}
        onFocus={() => {
          setFocused(true);
        }}
        onBlur={event => {
          // focus-within: only resume once focus has left the card entirely.
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setFocused(false);
          }
        }}
        className={cn(
          // Figma 6940:18935 chrome: #fbfbfb (mb-offwhite) card. 20px radius —
          // kept as an explicit pixel value (no Tailwind token sits close enough
          // to prefer over it): it's the app's de-facto card-family radius, used
          // verbatim by ~15 other card surfaces (QuestionCard, ProfileQuestionCard,
          // LiveHomeRecap, HelperFeedSkeleton, JoinGateFade...), and it isn't
          // concentric with anything inside this card (the image's own 12px
          // corner sits flush at the card edge and visually wins there; the pill
          // CTA is `rounded-full`, not a nested rectangle) — so rounding it to
          // 2xl/16px or 3xl/24px would only drift this card from its siblings.
          // A flex ROW (Dexter 2026-07-23): the image spans the card's FULL height
          // on the left (behind the teal right accent rail), the RIGHT COLUMN
          // (title → content → row-3) stacks beside it — see the wrappers below.
          'relative flex cursor-pointer rounded-[20px] border-r-2 border-mb-teal bg-mb-offwhite',
        )}
      >
        {/* Image block (Figma 6940:18937): 112px wide (w-28), spans the card's FULL
            height (self-stretch — the flex-row card's cross-axis), teal (mb-teal)
            left border, left-only 12px (rounded-l-xl) radii, clipped. Tapping it
            navigates via the ROOT's delegated onClick (whole-card tap) — it is not
            a button itself, so the body button stays the single AT control. The
            EVENT image fills it object-cover (same source + house placeholder as
            EventCardShell), OUT OF FLOW (absolute inset-0, EventCardShell's pattern):
            in flow, `h-full` against this auto-height span falls back to the
            picture's natural height, so a portrait cover stretched the card (TECH-824). */}
        <span className="relative w-28 shrink-0 self-stretch overflow-clip rounded-l-xl border-l-2 border-mb-teal">
          <ImgWithFallback
            src={encodeURI(coverUrl || '/meetball-logo.png')}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full bg-gray-100 object-cover"
            // Matches CommunityGridCard's banner fallback: on error (a bad R2
            // key, or offline before this session cached the logo), fall back
            // to a plain tinted box rather than a second request for the same
            // `/meetball-logo.png` URL that just failed. The wrapping <span>
            // carries no background of its own (unlike CommunityCard's boxed
            // avatar), so the box is rebuilt fresh here instead of via `null`.
            fallback={() => <div aria-hidden="true" className="absolute inset-0 bg-gray-100" />}
          />
          {/* left-1.5 (6px) — Figma spec'd 5px; rounded to the nearest scale step
              (matches the sibling top-1.5) rather than an arbitrary pixel value,
              per Dexter's scale-purity-over-pixel-exactness ruling (2026-07-23). */}
          {badge ? <span className="absolute left-1.5 top-1.5">{badge}</span> : null}
        </span>

        {/* Right column: a top-anchored stack that HUGS its content (Dexter
            2026-07-23 №2) — title → content → row-3 with ONE uniform gap-1.5 and
            modest py-2.5; NO justify distribution, NO spacers. min-w-0 so the
            title/content truncate; the column's natural height IS the card height
            (the image stretches to match, never dictates). */}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 py-2.5">
          {/* Top: the tappable body (title + content row). */}
          <div className="flex items-start gap-1 pr-3.5">
            <button
              type="button"
              onClick={onTap}
              className={cn(
                // No vertical padding or justify of its own (hug №2): the COLUMN
                // owns py, so the space under the content row stays the uniform
                // gap-1.5 — no pad stacking pushing row-3 down.
                'flex min-w-0 flex-1 flex-col gap-1.5 rounded-2xl pl-3.5 text-left',
                // Pressed cue (W1-3 M4): the wide body tints instead of scaling —
                // non-transform parity with the CTA press feedback (both
                // duration-[160ms] — kept in lockstep with the shared
                // PRESS_FEEDBACK_CLASSES value in liveMotion.ts, so it stays
                // arbitrary rather than rounding to the nearest duration step).
                'transition-colors duration-[160ms] ease-out active:bg-black/5',
                FOCUS_RING_CLASSES,
              )}
            >
              <span className="line-clamp-2 text-sm font-semibold text-black">{title}</span>
              {children}
            </button>
          </div>
          {cta ? (
            // Row 3 (Dexter 2026-07-23): the CTA/status is the RIGHT COLUMN's last
            // row, IMMEDIATELY under the content — the column's uniform gap is the
            // only space between them, and the column's py owns the bottom
            // clearance (no pb of its own; hug №2). pl-3.5 aligns with the body
            // button's left pad; pr-3.5 mirrors the top row.
            <div className="pl-3.5 pr-3.5">
              {cta.kind === 'status' ? (
                // Non-interactive muted status ("You're going") — no button, no hit
                // area, NATURAL height (hug №2: the old h-11 filler is what floated
                // the status low with dead space around it; a goer card is barely
                // taller than title+content+status and does NOT height-match the
                // CTA variant). Full COLUMN width, left-aligned.
                <span className="flex w-full items-center text-xs font-medium text-mb-gray-text">
                  {cta.label}
                </span>
              ) : (
                // ≥44px hit box (h-11) around the compact visual h-8 pill (§4.1 hit
                // areas; the WeMetRosterDrawer chip pattern). -my-1.5 collapses the
                // 12px of invisible tap padding OUT of layout (44 − 12 = the pill's
                // 32px), so the hit box never inflates the stack: the 6px of tap
                // slack above exactly fills the column's gap-1.5 — flush with the
                // body button, never overlapping it — and the 6px below sits inside
                // the column's pb. Full COLUMN width; a flex sibling of the body
                // button (never nested), so taps never double-fire.
                <button
                  type="button"
                  onClick={event => {
                    event.stopPropagation();
                    cta.onTap();
                  }}
                  className={cn(
                    '-my-1.5 flex h-11 w-full items-center rounded-full',
                    PRESS_FEEDBACK_CLASSES,
                    FOCUS_RING_CLASSES,
                  )}
                >
                  <span
                    className={cn(
                      'flex h-8 w-full items-center justify-center rounded-full border px-3 text-xs font-semibold whitespace-nowrap',
                      // `primary` reuses the event hero's shared filled-teal treatment
                      // (one source — W2.7) at the compact card scale; `soft` = the
                      // subdued recap chip. `w-full` + `justify-center` are layout
                      // modifiers layered on TOP of the shared class AT THIS CALL SITE
                      // ONLY — the shared `EVENT_JOIN_CTA_CLASS` constant and the
                      // hero's own hug-width CTA (`EventPageActions`) are untouched.
                      cta.kind === 'primary'
                        ? EVENT_JOIN_CTA_CLASS
                        : 'border-transparent bg-mb-yellow-soft text-gray-900',
                    )}
                  >
                    {cta.label}
                  </span>
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </LiveCardPauseContext>
  );
}
